<template>
  <div v-if="hasAccess">
    <div class="py-4 container-fluid">
      <ToastPrime position="bottom-left" />
      <div class="row">
        <div class="col-12">
          <div class="card" style="height: auto">
            <div class="card-header py-1 justify-content-start">
              <TabView v-model:activeIndex="tabIndex">
                <TabPanel header="Mail Configuration">
                  <SkeletonLoading
                    v-if="loading && tabIndex === 0"
                    class="px-4 pt-5"
                    :shapes="[
                      { type: 'line', width: '100%', height: '20px' },
                      { type: 'line', width: '80%', height: '20px' },
                      { type: 'line', width: '60%', height: '20px' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-3' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-2' },
                      { type: 'rect', width: '30%', height: '35px', class: 'mt-4' },
                    ]"
                  />
                  <form v-else @submit.prevent="saveMailConfig">
                    <div class="row">
                      <div
                        v-for="key in mailFieldKeys"
                        :key="key"
                        class="col-6"
                      >
                        <div class="form-group">
                          <label :for="`mail-${key}`">
                            {{ formatLabel(key) }}
                            <span class="text-danger">*</span>
                          </label>
                          <div v-if="isPasswordField(key)" class="input-group">
                            <input
                              :id="`mail-${key}`"
                              v-model.trim="mailConfig.data[key]"
                              :type="showPassword ? 'text' : 'password'"
                              class="form-control"
                              required
                            />
                            <span
                              class="input-group-text"
                              style="cursor: pointer"
                              @click="togglePasswordVisibility"
                            >
                              <i
                                :class="
                                  showPassword ? 'fa fa-eye' : 'fa fa-eye-slash'
                                "
                              ></i>
                            </span>
                          </div>
                          <div v-else>
                            <input
                              :id="`mail-${key}`"
                              v-model.trim="mailConfig.data[key]"
                              :type="getInputType(key)"
                              class="form-control"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="text-end my-3">
                      <button
                        v-if="hasMailConfigUpdatePermission && hasTestConnectionPermission"
                        type="button"
                        class="btn btn-secondary btn-sm me-2"
                        :disabled="isProcessing || loading"
                        @click="testConnection"
                      >
                        <span v-if="!isProcessing">Test Connection</span>
                        <span v-else>Processing...</span>
                      </button>
                      <button
                        v-if="hasMailConfigUpdatePermission"
                        type="submit"
                        class="btn bg-gradient-dark btn-sm"
                        :disabled="loading"
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </TabPanel>
                <TabPanel header="Maximum File Size">
                  <SkeletonLoading
                    v-if="loading && tabIndex === 1"
                    class="px-4 pt-5"
                    :shapes="[
                      { type: 'line', width: '100%', height: '20px' },
                      { type: 'line', width: '80%', height: '20px' },
                      { type: 'line', width: '60%', height: '20px' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-3' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-2' },
                      { type: 'rect', width: '30%', height: '35px', class: 'mt-4' },
                    ]"
                  />
                  <form v-else @submit.prevent="saveMaxSizeConfig">
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label for="maxFileSize"
                            >Max File Size (in KB)
                            <span class="text-danger">*</span></label
                          >
                          <input
                            id="maxFileSize"
                            v-model.number="maxSizeConfig.data.maximum_file"
                            type="number"
                            class="form-control"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div class="text-end my-3">
                      <button
                        v-if="hasMaxFileUpdatePermission"
                        type="submit"
                        class="btn bg-gradient-dark btn-sm"
                        :disabled="loading"
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </TabPanel>
                <TabPanel header="Manage Menu">
                  <div>
                    <SkeletonLoading
v-if="loading && tabIndex === 2" class="px-4 pt-5"
                      :shapes="[
                        { type: 'line', width: '100%', height: '20px' },
                        { type: 'line', width: '80%', height: '20px' },
                        { type: 'line', width: '60%', height: '20px' },
                        { type: 'rect', width: '100%', height: '40px', class: 'mt-3' },
                        { type: 'rect', width: '100%', height: '40px', class: 'mt-2' },
                        { type: 'rect', width: '30%', height: '35px', class: 'mt-4' },
                      ]"
                    />
                    <div v-else>
                      <Tree
                        :value="treeMenu"
                        class="w-100"
                      >
                        <template #default="slotProps">
                          <div class="d-flex align-items-center" style="min-width: 350px;">
                            <span class="me-3" style="flex: 1 1 auto;">{{ slotProps.node.label.name }}</span>
                            <InputSwitch
                              v-model="slotProps.node.label.statusModel"
                              :true-value="'active'"
                              :false-value="'inactive'"
                              :disabled="!hasManageMenuUpdatePermission"
                              style="flex-shrink: 0;"
                              @change="onStatusChange"
                            />
                          </div>
                        </template>
                      </Tree>
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header="Session Lifetime">
                  <SkeletonLoading
                    v-if="loading && tabIndex === 3"
                    class="px-4 pt-5"
                    :shapes="[
                      { type: 'line', width: '100%', height: '20px' },
                      { type: 'line', width: '80%', height: '20px' },
                      { type: 'line', width: '60%', height: '20px' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-3' },
                      { type: 'rect', width: '30%', height: '35px', class: 'mt-4' },
                    ]"
                  />
                  <form v-else @submit.prevent="saveSessionLifetimeConfig">
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label for="sessionLifetime">
                            Minute Session Lifetime
                            <span class="text-danger">*</span>
                          </label>
                          <input
                            id="sessionLifetime"
                            v-model.number="sessionLifetimeConfig.data.lifetime"
                            type="number"
                            class="form-control"
                            min="0"
                            step="1"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div class="text-end my-3">
                      <button
                        v-if="hasSessionLifetimeUpdatePermission"
                        type="submit"
                        class="btn bg-gradient-dark btn-sm"
                        :disabled="loading"
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </TabPanel>
                <TabPanel header="User Guide">
                  <SkeletonLoading
                    v-if="loading && tabIndex === 4"
                    class="px-4 pt-5"
                    :shapes="[
                      { type: 'line', width: '100%', height: '20px' },
                      { type: 'line', width: '80%', height: '20px' },
                      { type: 'line', width: '60%', height: '20px' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-3' },
                      { type: 'rect', width: '100%', height: '40px', class: 'mt-2' },
                      { type: 'rect', width: '30%', height: '35px', class: 'mt-4' },
                    ]"
                  />
                  <form v-else @submit.prevent="saveUserGuideConfig">
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label for="userGuideLink">
                            User Guide Link
                            <span class="text-danger">*</span>
                          </label>
                          <input
                            id="userGuideLink"
                            v-model.trim="userGuideConfig.data.link"
                            type="url"
                            class="form-control"
                            :class="{ 'is-invalid': errors.userGuideLink }"
                            required
                          />
                          <div v-if="errors.userGuideLink" class="invalid-feedback">
                            {{ errors.userGuideLink }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="text-end my-3">
                      <button
                        v-if="hasUserGuideUpdatePermission"
                        type="submit"
                        class="btn bg-gradient-dark btn-sm"
                        :disabled="loading"
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </TabPanel>
              </TabView>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import api from "@/helpers/api";
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import NoAccess from "@/components/NoAccess.vue";
import mailConfigurationService from "@/services/generalSettings/mailConfigurationService";
import menuService from "@/services/generalSettings/menuService";
import moment from "moment";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Tree from "primevue/tree";
import InputSwitch from "primevue/inputswitch";
import setMaxSize from "@/helpers/setMaxSize";
moment.locale("id");

export default {
  components: {
    SkeletonLoading,
    NoAccess,
    TabView,
    TabPanel,
    Tree,
    InputSwitch,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasAccess() {
      return this.authUserPermissions.includes('app-configuration-view');
    },
    hasMailConfigUpdatePermission() {
      return this.authUserPermissions.includes('app-configuration-mail-configuration-update');
    },
    hasTestConnectionPermission() {
      return this.authUserPermissions.includes('app-configuration-mail-configuration-test-connection');
    },
    hasMaxFileUpdatePermission() {
      return this.authUserPermissions.includes('app-configuration-maximum-file-update');
    },
    hasSessionLifetimeUpdatePermission() {
      return this.authUserPermissions.includes('app-configuration-session-lifetime-update');
    },
    hasUserGuideUpdatePermission() {
      return this.authUserPermissions.includes('app-configuration-user-guide-update');
    },
    hasManageMenuUpdatePermission() {
      return this.authUserPermissions.includes('app-configuration-manage-menu-update');
    },
    // Ambil semua key dari mailConfig.data (urutan sesuai object asli, tapi JS tidak jamin)
    mailFieldKeys() {
      if (!this.mailConfig.data || typeof this.mailConfig.data !== 'object') return [];
      return Object.keys(this.mailConfig.data);
    },
  },
  data() {
    return {
      isProcessing: false,
      loading: false,
      showPassword: false,
      tabIndex: null,
      mailConfig: {
        data: {},
        created_at: "",
        updated_at: "",
        code: "mail_configuration",
      },
      maxSizeConfig: {
        data: {
          maximum_file: 0,
        },
        created_at: "",
        updated_at: "",
        code: "maximum_file",
      },
      userGuideConfig: {
        data: {
          link: "",
        },
        created_at: "",
        updated_at: "",
        code: "user_guide",
      },
      sessionLifetimeConfig: {
        data: {
          session_lifetime: 0,
        },
        created_at: "",
        updated_at: "",
        code: "session_lifetime",
      },
      errors: {
        userGuideLink: null,
      },
      manageMenuList: [],
      treeMenu: [],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.tabIndex = 0;
    });

    if (this.hasAccess) {
      this.getMailConfig();
      this.getMaxSizeConfig();
      this.getManageMenuList();
      this.getUserGuideConfig();
      this.getSessionLifetimeConfig();
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    // Konversi key seperti "from_address" → "From Address"
    formatLabel(key) {
      return key
        .replace(/_/g, ' ')               // ganti underscore jadi spasi
        .replace(/\b\w/g, char => char.toUpperCase()); // kapitalisasi tiap kata
    },

    // Deteksi apakah field adalah password
    isPasswordField(key) {
      return key.toLowerCase().includes('password');
    },

    // Tentukan tipe input berdasarkan key
    getInputType(key) {
      const numberFields = ['port', 'timeout', 'max_file_size', 'maximum_file'];
      if (numberFields.some(f => key.toLowerCase().includes(f))) {
        return 'number';
      }
      return 'text';
    },

    // --- Sisa method tetap sama ---
    async getMailConfig() {
      this.loading = true;
      const code = "mail_configuration";
      try {
        const response = await api(this, mailConfigurationService.get(code), false);
        if (response && response.data) {
          this.mailConfig = { ...response.data, code };
        }
      } catch (error) {
        console.error("Error fetching mail configuration:", error);
      } finally {
        this.loading = false;
      }
    },
    async getMaxSizeConfig() {
      this.loading = true;
      try {
        const result = await setMaxSize();
        if (result) {
          this.maxSizeConfig.data       = result.data.data;
          this.maxSizeConfig.created_at = result.data.created_at;
          this.maxSizeConfig.updated_at = result.data.updated_at;
        }
      } catch (error) {
        this.$toastNotifPrime('error', "Erro", "Error fetching maximum file size.");
      } 
    },
    async saveMailConfig() {
      if (!this.hasMailConfigUpdatePermission) return;
      this.loading = true;
      try {
        const payload = {
          code: this.mailConfig.code,
          ...this.mailConfig.data,
        };
        const response = await api(
          this,
          mailConfigurationService.update(payload)
        );
        if (response && response.status === 200) {
          alert("Mail configuration updated successfully!");
        }
      } catch (error) {
        console.error("Error updating mail configuration:", error);
      } finally {
        this.loading = false;
      }
    },
    async saveMaxSizeConfig() {
      if (!this.hasMaxFileUpdatePermission) return;
      this.loading = true;
      try {
        const payload = {
          code: this.maxSizeConfig.code,
          maximum_file: this.maxSizeConfig.data.maximum_file,
        };
        const response = await api(
          this,
          mailConfigurationService.updateMaxSize(payload)
        );
        if (response && response.status === 200) {
          alert("Maximum file size updated successfully!");
        }
      } catch (error) {
        console.error("Error updating maximum file size:", error);
      } finally {
        this.loading = false;
      }
    },
    async testConnection() {
      if (!this.hasMailConfigUpdatePermission || !this.hasTestConnectionPermission) return;
      this.isProcessing = true;
      try {
        // Save config dulu sebelum test
        const payload = {
          code: this.mailConfig.code,
          ...this.mailConfig.data,
        };
        await api(this, mailConfigurationService.update(payload), false);
        
        // Baru test connection
        const response = await api(
          this,
          mailConfigurationService.testConnection()
        );
        if (response && response.status === 200) {
          alert("Mail connection test successful!");
        }
      } catch (error) {
        console.error("Error testing mail connection:", error);
      } finally {
        this.isProcessing = false;
      }
    },
    async getSessionLifetimeConfig() {
      this.loading = true;
      const code = "session_lifetime";
      try {
        const response = await api(this, mailConfigurationService.get(code), false);
        if (response && response.data) {
          this.sessionLifetimeConfig = { ...response.data, code };
        }
      } catch (error) {
        console.error("Error fetching session lifetime configuration:", error);
      } finally {
        this.loading = false;
      }
    },
    async saveSessionLifetimeConfig() {
      if (!this.hasSessionLifetimeUpdatePermission) return;
      this.loading = true;
      try {
        const payload = {
          code: this.sessionLifetimeConfig.code,
          lifetime: this.sessionLifetimeConfig.data.lifetime,
        };
        const response = await api(
          this,
          mailConfigurationService.update(payload)
        );
        if (response && response.status === 200) {
          alert("Session lifetime configuration updated successfully!");
        }
      } catch (error) {
        console.error("Error updating session lifetime configuration:", error);
      } finally {
        this.loading = false;
      }
    },
    async getManageMenuList() {
      this.loading = true;
      try {
        const res = await menuService.getManageMenu();
        if (res && res.data && res.data.data && Array.isArray(res.data.data)) {
          this.manageMenuList = res.data.data;
          this.treeMenu = this.manageMenuList.map((menu) => ({
            key: menu.name,
            label: {
              name: menu.name,
              status: menu.status,
              statusModel: menu.status,
            },
            icon: "pi pi-folder",
            data: menu,
            children: (menu.sub_menu || []).map((sub) => ({
              key: menu.name + "-" + sub.name,
              label: {
                name: sub.name,
                status: sub.status,
                statusModel: sub.status,
              },
              icon: "pi pi-file",
              data: sub,
            })),
          }));
        }
      } catch (error) {
        console.error("Error fetching manage menu:", error);
      } finally {
        this.loading = false;
      }
    },
    onStatusChange() {
      if (!this.hasManageMenuUpdatePermission) return;
      const payload = this.treeMenu.map((menu) => ({
        name: menu.label.name,
        status: menu.label.statusModel,
        sub_menu: (menu.children || []).map((sub) => ({
          name: sub.label.name,
          status: sub.label.statusModel,
        })),
      }));

      this.loading = true;
      api(this, menuService.updateManageMenuStatus("manage-menu", payload))
        .then((response) => {
          if (response && response.status === 200) {
            this.$toast.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Menu status updated successfully!',
              life: 3000
            });
          }
          this.saveSubmenuToAuthVuex(payload);
        })
        .catch((error) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update menu status!',
            life: 3000
          });
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveSubmenuToAuthVuex(payload) {
      const menuNav = payload.reduce((acc, menu) => {
        acc[menu.name] = { status: menu.status === "active" };
        return acc;
      }, {});

      const subMenuNav = payload.reduce((acc, menu) => {
        menu.sub_menu.forEach((sub) => {
          acc[sub.name] = { status: sub.status === "active" };
        });
        return acc;
      }, {});

      this.$store.dispatch("auth/setMenuNav", menuNav);
      this.$store.dispatch("auth/setSubMenuNav", subMenuNav);
    },
    async getUserGuideConfig() {
      this.loading = true;
      const code = "user_guide";
      try {
        const response = await api(this, mailConfigurationService.get(code), false);
        if (response && response.data) {
          // Ambil data dari kolom data dalam respons
          this.userGuideConfig = { 
            ...response.data,
            data: response.data.data || { link: "" },
            code: response.data.code || "user_guide"
          };
        }
      } catch (error) {
        console.error("Error fetching user guide configuration:", error);
      } finally {
        this.loading = false;
      }
    },
    async saveUserGuideConfig() {
      if (!this.hasUserGuideUpdatePermission) return;
      // Reset error sebelum validasi
      this.errors.userGuideLink = null;
      
      // Validasi input
      if (!this.userGuideConfig.data.link) {
        this.errors.userGuideLink = "User Guide link is required";
        return;
      }
      
      // Validasi format URL
      const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      if (!urlPattern.test(this.userGuideConfig.data.link)) {
        this.errors.userGuideLink = "Please enter a valid URL (must start with http:// or https://)";
        return;
      }
      
      this.loading = true;
      try {
        const payload = {
          code: this.userGuideConfig.code,
          link: this.userGuideConfig.data.link,
        };
        const response = await api(
          this,
          mailConfigurationService.update(payload)
        );
        if (response && response.status === 200) {
          alert("User guide configuration updated successfully!");
        }
      } catch (error) {
        console.error("Error updating user guide configuration:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
/* Use deep selector to override PrimeVue */
.p-tabview :deep(.p-tabview-ink-bar) {
  display: none !important;
}

.p-tabview :deep(.p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  border-bottom: 3px solid #0d6efd !important;
  color: #0d6efd !important;
  font-weight: 600 !important;
}

.p-tabview :deep(.p-tabview-nav li .p-tabview-nav-link) {
  border-bottom: 3px solid transparent !important;
  transition: all 0.3s !important;
}
</style>